import GameCore from "../GameCore";

interface VFXInstance {
  sprite: Phaser.GameObjects.Sprite | null;
  inUse: boolean;
  type: VFXType;
  cleanup?: () => void;
}

export enum VFXType {
  DEATH_ANIMATION = "death_animation",
  DAMAGE_FLASH = "damage_flash",
}

/**
 * VFX Pool for efficient visual effects management
 * Handles sprite animations, explosions, hit effects, etc.
 */
export default class VFXPool {
  private scene: Phaser.Scene;
  private vfxInstances: Map<VFXType, VFXInstance[]> = new Map();
  private maxInstancesPerType: number = 10;
  private totalMaxInstances: number = 50;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.initializePools();
  }

  private initializePools(): void {
    // Initialize pools for each VFX type explicitly
    const types = [VFXType.DEATH_ANIMATION, VFXType.DAMAGE_FLASH];

    types.forEach((type) => {
      this.vfxInstances.set(type, []);
    });

    // Debug: verify initialization
    console.log(
      "VFXPool initialized with types:",
      Array.from(this.vfxInstances.keys())
    );
  }

  /**
   * Show death animation at position
   */
  showDeathAnimation(x: number, y: number, scale: number = 1): void {
    const instance = this.getVFXInstance(VFXType.DEATH_ANIMATION);
    if (!instance) return;

    this.configureDeathAnimation(instance, x, y, scale);
  }

  /**
   * Show damage flash effect on target (red tint)
   */
  showDamageFlash(
    target: Phaser.GameObjects.GameObject,
    duration: number = 120
  ): void {
    const instance = this.getVFXInstance(VFXType.DAMAGE_FLASH);
    if (!instance) return;

    this.configureDamageFlash(instance, target, duration);
  }

  private getVFXInstance(type: VFXType): VFXInstance | null {
    const instances = this.vfxInstances.get(type);

    // Safety check: if type not initialized, initialize it now
    if (!instances) {
      console.warn(`VFXPool: Type ${type} not initialized, initializing now`);
      console.warn("Available types:", Array.from(this.vfxInstances.keys()));
      this.vfxInstances.set(type, []);
      return this.getVFXInstance(type); // Retry after initialization
    }

    // Find available instance
    let instance = instances.find((inst) => !inst.inUse);

    if (!instance) {
      // Check if we can create new instance
      if (instances.length >= this.maxInstancesPerType) {
        // Reuse oldest instance
        const oldestInstance = instances[0];
        this.stopVFXInstance(oldestInstance);
        instance = oldestInstance;
      } else {
        // Check total limit
        const totalInstances = Array.from(this.vfxInstances.values()).reduce(
          (total, arr) => total + arr.length,
          0
        );

        if (totalInstances >= this.totalMaxInstances) {
          // Find and reuse oldest instance across all types
          const oldestInstance = this.findOldestInstance();
          if (oldestInstance) {
            this.stopVFXInstance(oldestInstance);
            instance = oldestInstance;
            instance.type = type;
          } else {
            return null;
          }
        } else {
          // Create new instance
          instance = this.createVFXInstance(type);
          instances.push(instance);
        }
      }
    }

    return instance;
  }

  private createVFXInstance(type: VFXType): VFXInstance {
    return {
      sprite: null,
      inUse: false,
      type,
    };
  }

  private configureDeathAnimation(
    instance: VFXInstance,
    x: number,
    y: number,
    scale: number
  ): void {
    // Create or reuse sprite
    if (!instance.sprite) {
      instance.sprite = this.scene.add.sprite(x, y, "enemy_death");
    } else {
      instance.sprite.setPosition(x, y);
    }

    instance.sprite.setScale(scale * GameCore.PixelScale);
    instance.sprite.setVisible(true);
    instance.inUse = true;

    // Play death animation
    instance.sprite.play("enemy_death_animation");

    const cleanup = () => {
      if (instance.sprite) {
        instance.sprite.setVisible(false);
        instance.sprite.stop();
      }
      instance.inUse = false;
    };

    instance.cleanup = cleanup;
    instance.sprite.once("animationcomplete", cleanup);
  }

  private configureDamageFlash(
    instance: VFXInstance,
    target: Phaser.GameObjects.GameObject,
    duration: number
  ): void {
    // Apply red tint for damage
    if (target && (target as any).setTintFill) {
      (target as any).setTintFill(0xff0000); // Red tint
      instance.inUse = true;

      const cleanup = () => {
        if (target && (target as any).clearTint) {
          (target as any).clearTint();
        }
        instance.inUse = false;
      };

      instance.cleanup = cleanup;
      this.scene.time.delayedCall(duration, cleanup);
    }
  }

  private stopVFXInstance(instance: VFXInstance): void {
    if (instance.cleanup) {
      instance.cleanup();
    }
    if (instance.sprite) {
      instance.sprite.setVisible(false);
      if (instance.sprite.stop) {
        instance.sprite.stop();
      }
    }
    instance.inUse = false;
  }

  private findOldestInstance(): VFXInstance | null {
    for (const instances of this.vfxInstances.values()) {
      for (const instance of instances) {
        if (instance.inUse) {
          return instance;
        }
      }
    }
    return null;
  }

  /**
   * Stop all VFX effects of a specific type
   */
  stopAllVFX(type?: VFXType): void {
    if (type) {
      const instances = this.vfxInstances.get(type);
      if (instances) {
        instances.forEach((instance) => this.stopVFXInstance(instance));
      }
    } else {
      // Stop all VFX
      for (const instances of this.vfxInstances.values()) {
        instances.forEach((instance) => this.stopVFXInstance(instance));
      }
    }
  }

  /**
   * Cleanup and dispose of all VFX instances
   */
  destroy(): void {
    for (const instances of this.vfxInstances.values()) {
      instances.forEach((instance) => {
        this.stopVFXInstance(instance);
        if (instance.sprite && instance.sprite.destroy) {
          instance.sprite.destroy();
        }
      });
    }
    this.vfxInstances.clear();
  }

  /**
   * Get pool statistics for monitoring
   */
  getStats(): {
    totalInstances: number;
    activeInstances: number;
    instancesByType: Record<string, number>;
  } {
    let totalInstances = 0;
    let activeInstances = 0;
    const instancesByType: Record<string, number> = {};

    for (const [type, instances] of this.vfxInstances) {
      instancesByType[type] = instances.length;
      totalInstances += instances.length;
      activeInstances += instances.filter((instance) => instance.inUse).length;
    }

    return {
      totalInstances,
      activeInstances,
      instancesByType,
    };
  }
}
